// src/components/Goals/Goals.js
import { useState, useEffect, useRef } from 'react';
import api from '../../Services/Api';
import {
    calculateGoalProgress,
    getDaysRemaining,
    getGoalStatus,
    getGoalTypeLabel,
    getGoalUnitLabel
} from './goalHelpers';
import styles from './Goals.module.css';

const GOAL_OPTIONS = [
    { value: 'weekly', label: 'Weekly Goal (clean days per week)' },
    { value: 'monthly', label: 'Monthly Goal (clean days per month)' },
];

function Goals({ userData }) {
    const [goals, setGoals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    const [formData, setFormData] = useState({
        time_period: 'weekly',
        max_goal: 1,
        active: true
    });
    const [dateRange, setDateRange] = useState({ start: '', end: '' });
    const [goalProgress, setGoalProgress] = useState({});

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Auto-set initial date range
    useEffect(() => {
        handleSelectChange('weekly');
    }, []);

    const handleSelectChange = (value) => {
        const today = new Date();
        let startDate = today;
        let endDate = '';

        if (value === 'weekly') {
            endDate = new Date(today);
            endDate.setDate(today.getDate() + 6);
        } else if (value === 'monthly') {
            endDate = new Date(today);
            endDate.setDate(today.getDate() + 29);
        }

        const startStr = startDate.toISOString().split('T')[0];
        const endStr = endDate ? endDate.toISOString().split('T')[0] : '';

        setDateRange({ start: startStr, end: endStr });
        setFormData({ ...formData, time_period: value });
        setIsDropdownOpen(false);
    };

    useEffect(() => {
        fetchGoals();
    }, []);

    const fetchGoals = async () => {
        try {
            setLoading(true);
            const response = await api.get('goals/active/');
            const goalsData = response.data;
            setGoals(goalsData);

            const progressData = {};
            for (const goal of goalsData) {
                const progressResult = await calculateGoalProgress(goal);
                progressData[goal.id] = progressResult;
            }
            setGoalProgress(progressData);
            setError(null);
        } catch (err) {
            console.error('Error fetching goals:', err);
            if (err.response?.status === 404) {
                setGoals([]);
                setGoalProgress({});
            } else {
                setError('Failed to load goals. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const refreshGoalProgress = async () => {
        const progressData = {};
        for (const goal of goals) {
            const progressResult = await calculateGoalProgress(goal);
            progressData[goal.id] = progressResult;
        }
        setGoalProgress(progressData);
    };

    // Separate active and completed goals
    const { activeGoals, completedGoals } = goals.reduce((acc, goal) => {
        const progressData = goalProgress[goal.id] || { progress: 0 };
        const today = new Date();
        const goalEnd = goal.end_date ? new Date(goal.end_date) : null;
        const isPeriodOver = goalEnd && today > goalEnd;
        const isCompleted = progressData.progress >= 100 || (isPeriodOver && progressData.progress >= 100);
        
        if (isCompleted) {
            acc.completedGoals.push(goal);
        } else {
            acc.activeGoals.push(goal);
        }
        return acc;
    }, { activeGoals: [], completedGoals: [] });

    const getDisplayEndDate = (goal) => {
        if (goal.end_date) return goal.end_date;

        const start = new Date(goal.start_date);

        if (goal.time_period === 'weekly') {
            start.setDate(start.getDate() + 6);
            return start;
        }

        if (goal.time_period === 'monthly') {
            start.setDate(start.getDate() + 29);
            return start;
        }

        return null; // daily or ongoing
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        const numValue = name === 'max_goal' ? parseInt(value) || 1 : value; // ✅ Fallback to 1
        setFormData({
            ...formData,
            [name]: numValue
        });
    };

    const handleCreateGoal = async (e) => {
        e.preventDefault();
        if (!dateRange.start || !dateRange.end) {
            alert('Invalid goal period. Please select a valid goal type.');
            return;
        }
        const payload = {
            ...formData,
            start_date: dateRange.start,
            end_date: dateRange.end || null
        };

        try {
            await api.post('goals/create/', payload);
            setShowCreateForm(false);
            setFormData({ time_period: 'daily', max_goal: 1, active: true });
            setDateRange({ start: '', end: '' });
            await fetchGoals();
        } catch (err) {
            console.error('Error creating goal:', err);
            alert('Failed to create goal. Please try again.');
        }
    };

    const handleDeactivateGoal = async (goalId) => {
        if (window.confirm('Are you sure you want to deactivate this goal?')) {
            try {
                await api.put(`goals/deactivate/${goalId}/`);
                await fetchGoals();
            } catch (err) {
                console.error('Error deactivating goal:', err);
                alert('Failed to deactivate goal. Please try again.');
            }
        }
    };

    const renderProgressBar = (goalId, progress) => {
        const status = getGoalStatus(progress);
        return (
            <div className={styles.progressBarContainer}>
                <div
                    className={styles.progressBar}
                    style={{ width: `${progress}%`, backgroundColor: status.color }}
                />
            </div>
        );
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'Ongoing';
        return new Date(dateStr).toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric',
            year: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className={styles.goalsContainer}>
                <div className={styles.loading}>
                    <div className={styles.spinner}></div>
                    <p>Loading your goals...</p>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.goalsContainer}>
            <div className={styles.header}>
                <h1>Goals & Targets</h1>
                <p className={styles.subtitle}>
                    Set targets to stay motivated and track your progress
                </p>
                <button
                    className={styles.refreshButton}
                    onClick={refreshGoalProgress}
                    title="Refresh progress calculations"
                >
                    ↻ Refresh Progress
                </button>
            </div>

            <div className={styles.createGoalSection}>
                <button
                    className={styles.createGoalButton}
                    onClick={() => setShowCreateForm(!showCreateForm)}
                >
                    {showCreateForm ? 'Cancel' : '+ Create New Goal'}
                </button>

                {showCreateForm && (
                    <div className={styles.createForm}>
                        <h3>Create New Goal</h3>
                        <form onSubmit={handleCreateGoal}>
                            <div className={styles.formGroup}>
                                <label>Goal Type</label>
                                <div className={styles.customSelect} ref={dropdownRef}>
                                    <div
                                        className={styles.selectControl}
                                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                    >
                                        <span>
                                            {GOAL_OPTIONS.find(opt => opt.value === formData.time_period)?.label}
                                        </span>
                                        <span className={styles.selectArrow}>▼</span>
                                    </div>
                                    {isDropdownOpen && (
                                        <ul className={styles.selectOptions}>
                                            {GOAL_OPTIONS.map(option => (
                                                <li
                                                    key={option.value}
                                                    className={styles.selectOption}
                                                    onClick={() => handleSelectChange(option.value)}
                                                >
                                                    {option.label}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>

                            <div className={styles.formGroup}>
                                <label>Target Value</label>
                                <input
                                    type="number"
                                    name="max_goal"
                                    value={formData.max_goal}
                                    onChange={handleInputChange}
                                    min="1"
                                    max={formData.time_period === 'weekly' ? 7 :
                                        formData.time_period === 'monthly' ? 30 : 10}
                                    required
                                    className={styles.formInput}
                                />
                                <small className={styles.helperText}>
                                    {formData.time_period === 'weekly' && 'Target clean days in next 7 days (max 7)'}
                                    {formData.time_period === 'monthly' && 'Target clean days in next 30 days (max 30)'}
                                </small>
                            </div>

                            {(formData.time_period === 'weekly' || formData.time_period === 'monthly') && (
                                <div className={styles.formGroup}>
                                    <label>Goal Period</label>
                                    <div className={styles.readOnlyDateRange}>
                                        {dateRange.start} → {dateRange.end}
                                    </div>
                                </div>
                            )}

                            <button type="submit" className={styles.submitButton}>
                                Create Goal
                            </button>
                        </form>
                    </div>
                )}
            </div>

            {error && (
                <div className={styles.error}>
                    {error}
                </div>
            )}

            <div className={styles.goalsList}>
                {goals.length === 0 ? (
                    <div className={styles.noGoals}>
                        <div className={styles.noGoalsIcon}>🎯</div>
                        <h3>No Active Goals</h3>
                        <p>Create your first goal to start tracking your progress!</p>
                        <p className={styles.noGoalsTip}>
                            Tip: Start with a 3-day streak goal to build momentum
                        </p>
                    </div>
                ) : (
                    <>
                        {/* Active Goals Section */}
                        {activeGoals.length > 0 && (
                            <>
                                <h2 className={styles.sectionTitle}>Active Goals</h2>
                                <div className={styles.goalsGrid}>
                                    {activeGoals.map(goal => {
                                        const progressData = goalProgress[goal.id] || { progress: 0, cleanDays: 0 };
                                        const { progress, cleanDays } = progressData;
                                        const status = getGoalStatus(progress);
                                        const daysRemaining = getDaysRemaining(goal.end_date);

                                        return (
                                            <div key={goal.id} className={styles.goalCard}>
                                                <div className={styles.goalHeader}>
                                                    <div className={styles.goalType}>
                                                        <span className={styles.goalTypeIcon}>
                                                            {goal.time_period === 'weekly' && '📆'}
                                                            {goal.time_period === 'monthly' && '📊'}
                                                        </span>
                                                        <span className={styles.goalTypeLabel}>
                                                            {getGoalTypeLabel(goal.time_period)}
                                                        </span>
                                                    </div>
                                                    <button
                                                        className={styles.deactivateButton}
                                                        onClick={() => handleDeactivateGoal(goal.id)}
                                                        title="Deactivate goal"
                                                    >
                                                        ×
                                                    </button>
                                                </div>

                                                <div className={styles.goalContent}>
                                                    <div className={styles.goalMainStat}>
                                                        <span className={styles.cleanDaysCount}>{cleanDays}</span>
                                                        <span className={styles.cleanDaysLabel}>
                                                            / {goal.max_goal} clean days
                                                        </span>
                                                    </div>

                                                    <div className={styles.progressSection}>
                                                        {renderProgressBar(goal.id, progress)}
                                                        <div className={styles.progressMeta}>
                                                            <span 
                                                                className={styles.statusText}
                                                                style={{ color: status.color }}
                                                            >
                                                                {status.message}
                                                            </span>
                                                            {daysRemaining !== null && daysRemaining > 0 && (
                                                                <span className={styles.daysLeft}>
                                                                    {daysRemaining}d left
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className={styles.goalDates}>
                                                        <span>{formatDate(goal.start_date)}</span>
                                                        <span className={styles.dateSeparator}>→</span>
                                                        <span>{formatDate(getDisplayEndDate(goal))}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}

                        {/* Completed Goals Section */}
                        {completedGoals.length > 0 && (
                            <>
                                <h2 className={`${styles.sectionTitle} ${styles.completedTitle}`}>
                                    ✓ Completed Goals
                                </h2>
                                <div className={styles.goalsGrid}>
                                    {completedGoals.map(goal => {
                                        const progressData = goalProgress[goal.id] || { progress: 0, cleanDays: 0 };
                                        const { cleanDays } = progressData;

                                        return (
                                            <div key={goal.id} className={`${styles.goalCard} ${styles.completedCard}`}>
                                                <div className={styles.goalHeader}>
                                                    <div className={styles.goalType}>
                                                        <span className={styles.goalTypeIcon}>🎉</span>
                                                        <span className={styles.goalTypeLabel}>
                                                            {getGoalTypeLabel(goal.time_period)}
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className={styles.goalContent}>
                                                    <div className={styles.goalMainStat}>
                                                        <span className={styles.cleanDaysCount}>{cleanDays}</span>
                                                        <span className={styles.cleanDaysLabel}>
                                                            / {goal.max_goal} clean days
                                                        </span>
                                                    </div>

                                                    <div className={styles.completedBadge}>
                                                        Goal Achieved!
                                                    </div>

                                                    <div className={styles.goalDates}>
                                                        <span>{formatDate(goal.start_date)}</span>
                                                        <span className={styles.dateSeparator}>→</span>
                                                        <span>{formatDate(getDisplayEndDate(goal))}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}

                        {activeGoals.length === 0 && completedGoals.length > 0 && (
                            <div className={styles.allCompletedMessage}>
                                <p>🎉 All goals completed! Create a new one to keep the momentum going.</p>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default Goals;